import { useState, useEffect, useCallback } from 'react'
import { supabase } from './lib/supabaseClient'
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement } from 'chart.js'
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2'
import * as XLSX from 'xlsx'
import WhatsAppButton from './components/WhatsAppButton'
import MessagingSystem from './components/MessagingSystem'
import PortalLayout from './components/PortalLayout'
import ChartOfAccounts from './components/ChartOfAccounts'
import BalanceSheet from './components/BalanceSheet'
import { responsiveStyles } from './styles/responsiveStyles'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement)

function AccountingPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [transactions, setTransactions] = useState([])
  const [salaries, setSalaries] = useState([])
  const [invoices, setInvoices] = useState([])
  const [budgets, setBudgets] = useState([])
  const [taxReturns, setTaxReturns] = useState([])
  const [documents, setDocuments] = useState([])
  const [agents, setAgents] = useState([])
  const [clients, setClients] = useState([])
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [showConverterModal, setShowConverterModal] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('accounting')
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [uploading, setUploading] = useState(false)
  const [reportData, setReportData] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState('monthly')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedQuarter, setSelectedQuarter] = useState(1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [errorMsg, setErrorMsg] = useState('')
  const [converterAmount, setConverterAmount] = useState(1)
  const [converterFrom, setConverterFrom] = useState('USD')
  const [converterTo, setConverterTo] = useState('NGN')
  const [convertedAmount, setConvertedAmount] = useState(null)
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1.1, NGN: 0.00065 })
  const [emailCompose, setEmailCompose] = useState({ to: '', subject: '', body: '', client_id: null, invoice_id: '' })
  const [selectedClient, setSelectedClient] = useState(null)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [selectedSalary, setSelectedSalary] = useState(null)
  const [approvalNote, setApprovalNote] = useState('')
  const [emailHistory, setEmailHistory] = useState([]) 


  // P&L Statement state
  const [plStatement, setPlStatement] = useState({
    revenue: 0,
    cogs: 0,
    operatingExpenses: 0,
    otherIncome: 0,
    otherExpenses: 0,
    taxRate: 25
  })

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'cashflow', label: 'Cash Flow', icon: '💵' },
    { id: 'profitloss', label: 'P&L Statement', icon: '📈' },
    { id: 'chartofaccounts', label: 'Chart of Accounts', icon: '📚' },
    { id: 'balancesheet', label: 'Balance Sheet', icon: '⚖️' },
    { id: 'transactions', label: 'Transactions', icon: '📝' },
    { id: 'salaries', label: 'Salaries', icon: '💰' },
    { id: 'invoices', label: 'Invoices', icon: '📄' },
    { id: 'budgets', label: 'Budgets', icon: '📋' },
    { id: 'taxReturns', label: 'Tax Returns', icon: '🏛️' },
    { id: 'documents', label: 'Documents', icon: '📎' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' },
    { id: 'emails', label: 'Emails', icon: '📧' }
  ]

  const currencies = ['USD', 'EUR', 'NGN']
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const expenseCategories = ['Rent', 'Utilities', 'Marketing', 'Maintenance', 'Legal', 'Insurance', 'Software', 'Travel', 'Salaries', 'Office Supplies', 'Taxes', 'Other']
  const incomeCategories = ['Property Sale', 'Commission', 'Rental Income', 'Consulting', 'Management Fees', 'Interest', 'Other']

  // Get company ID from session
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) {
        console.log('No user ID, redirecting to login')
        return
      }

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .single()

      if (error) {
        console.error('Error fetching company_id:', error)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        return
      }

      setCompanyId(userData.company_id)
      
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .single()
      
      if (company) {
        setCompanyName(company.name)
      }
      
      setLoading(false)
    }

    getCompanyId()
  }, [session?.user?.id])

  // Fetch all data when companyId is available
  useEffect(() => {
    if (companyId) {
      fetchData()
      fetchUnreadMessages()
      fetchExchangeRates()
      fetchEmails()
    }
  }, [activeTab, companyId])

  const fetchExchangeRates = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD')
      const data = await response.json()
      setExchangeRates({
        USD: 1,
        EUR: data.rates.EUR || 1.1,
        NGN: data.rates.NGN || 0.00065
      })
    } catch (error) {
      console.error('Exchange rate fetch error:', error)
    }
  }

  const fetchUnreadMessages = async () => {
    if (!companyId) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)
    
    setUnreadCount(count || 0)
  }

  const fetchData = async () => {
    if (!companyId) return
    setLoading(true)
    setErrorMsg('')

    try {
      // Fetch transactions
      const { data: transData, error: transError } = await supabase
        .from('transactions')
        .select('*')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false })
      if (transError) throw transError
      console.log('Transactions fetched:', transData?.length || 0)
      setTransactions(transData || [])

      // Fetch salaries
      const { data: salData, error: salError } = await supabase
        .from('salaries')
        .select('*, users!agent_id(email)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      if (salError) throw salError
      console.log('Salaries fetched:', salData?.length || 0)
      setSalaries(salData || [])

      // Fetch invoices
      const { data: invData, error: invError } = await supabase
        .from('invoices')
        .select('*, clients(full_name)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      if (invError) throw invError
      setInvoices(invData || [])

      // Fetch budgets
      const { data: budData, error: budError } = await supabase
        .from('budgets')
        .select('*')
        .eq('company_id', companyId)
        .order('year', { ascending: false })
        .order('month', { ascending: false })
      if (budError) throw budError
      setBudgets(budData || [])

      // Fetch tax returns
      const { data: taxData, error: taxError } = await supabase
        .from('tax_returns')
        .select('*')
        .eq('company_id', companyId)
      if (taxError) throw taxError
      setTaxReturns(taxData || [])

      // Fetch documents
      const { data: docData, error: docError } = await supabase
        .from('documents')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
      if (docError) throw docError
      setDocuments(docData || [])

      // Fetch agents
      const { data: agentData, error: agentError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .eq('company_id', companyId)
        .eq('role', 'agent')
      if (agentError) throw agentError
      setAgents(agentData?.length ? agentData : [{ id: session.user.id, email: session.user.email, first_name: '', last_name: '' }])

      // Fetch clients
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id, full_name, email, phone')
        .eq('company_id', companyId)
      if (clientError) throw clientError
      setClients(clientData || [])

    } catch (err) {
      console.error('Fetch error:', err)
      setErrorMsg(err.message)
    }
    setLoading(false)
  }

  // Calculate totals
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + (t.amount || 0), 0)
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0)
  const netProfit = totalIncome - totalExpense
  const pendingSalaries = salaries.filter(s => s.status === 'pending').reduce((s, t) => s + (t.net_salary || 0), 0)
  const accountsReceivable = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.total || 0), 0)

  // P&L Calculations
  const calculatePL = () => {
    const revenue = plStatement.revenue || totalIncome
    const cogs = plStatement.cogs || 0
    const operatingExpenses = plStatement.operatingExpenses || totalExpense
    const otherIncome = plStatement.otherIncome || 0
    const otherExpenses = plStatement.otherExpenses || 0
    
    const grossProfit = revenue - cogs
    const operatingProfit = grossProfit - operatingExpenses
    const totalOtherIncome = otherIncome - otherExpenses
    const netProfitBeforeTax = operatingProfit + totalOtherIncome
    const taxAmount = (netProfitBeforeTax * plStatement.taxRate) / 100
    const netProfitAfterTax = netProfitBeforeTax - taxAmount
    
    return {
      revenue,
      cogs,
      grossProfit,
      operatingExpenses,
      operatingProfit,
      otherIncome,
      otherExpenses,
      totalOtherIncome,
      netProfitBeforeTax,
      taxRate: plStatement.taxRate,
      taxAmount,
      netProfitAfterTax
    }
  }

  // Chart data functions
  const getMonthlyData = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const incomeData = monthNames.map((_, i) => {
      return transactions.filter(t => t.type === 'income' && new Date(t.transaction_date).getMonth() === i && new Date(t.transaction_date).getFullYear() === selectedYear)
        .reduce((s, t) => s + (t.amount || 0), 0)
    })
    const expenseData = monthNames.map((_, i) => {
      return transactions.filter(t => t.type === 'expense' && new Date(t.transaction_date).getMonth() === i && new Date(t.transaction_date).getFullYear() === selectedYear)
        .reduce((s, t) => s + (t.amount || 0), 0)
    })
    return { monthNames, incomeData, expenseData }
  }

  const getIncomeByCategoryData = () => {
    const incomeTransactions = transactions.filter(t => t.type === 'income')
    const categories = [...new Set(incomeTransactions.map(t => t.category))]
    const amounts = categories.map(cat => 
      incomeTransactions.filter(t => t.category === cat).reduce((s, t) => s + (t.amount || 0), 0)
    )
    const colors = ['#059669', '#2c7da0', '#1e3a5f', '#f59e0b', '#8b5cf6', '#ec489a', '#14b8a6', '#f97316']
    return { categories, amounts, colors }
  }

  const getExpenseByCategoryData = () => {
    const expenseTransactions = transactions.filter(t => t.type === 'expense')
    const categories = [...new Set(expenseTransactions.map(t => t.category))]
    const amounts = categories.map(cat => 
      expenseTransactions.filter(t => t.category === cat).reduce((s, t) => s + (t.amount || 0), 0)
    )
    const colors = ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#059669', '#0891b2', '#4f46e5']
    return { categories, amounts, colors }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
       
      if (activeTab === 'transactions') {
        const data = { 
          description: formData.description || '',
          amount: parseFloat(formData.amount) || 0,
          type: formData.type || 'expense',
          category: formData.category || '',
          currency: formData.currency || 'USD',
          transaction_date: formData.transaction_date || new Date().toISOString().split('T')[0],
          notes: formData.notes || '',
          company_id: companyId
        }
        
        console.log('Saving transaction data:', data)
        
        let result
        if (editingItem) {
          result = await supabase
            .from('transactions')
            .update(data)
            .eq('id', editingItem.id)
        } else {
          result = await supabase
            .from('transactions')
            .insert([data])
        }
        
        if (result.error) {
          console.error('Transaction error:', result.error)
          throw result.error
        }
        
        alert('Transaction saved successfully!')
      } 
      else if (activeTab === 'salaries') {
        const month = parseInt(formData.month)
        const year = parseInt(formData.year)
        
        if (!month || !year || isNaN(month) || isNaN(year)) {
          alert('Please select both month and year')
          setLoading(false)
          return
        }
        
        if (!formData.agent_id) {
          alert('Please select an agent')
          setLoading(false)
          return
        }
        
        const netSalary = (parseFloat(formData.base_salary) || 0) + 
                         (parseFloat(formData.commission) || 0) + 
                         (parseFloat(formData.bonus) || 0) - 
                         (parseFloat(formData.deductions) || 0)
        
        const salaryData = { 
          agent_id: formData.agent_id,
          month: month,
          year: year,
          base_salary: parseFloat(formData.base_salary) || 0,
          commission: parseFloat(formData.commission) || 0,
          bonus: parseFloat(formData.bonus) || 0,
          deductions: parseFloat(formData.deductions) || 0,
          net_salary: netSalary,
          currency: formData.currency || 'USD',
          status: 'pending',
          company_id: companyId
        }
        
        console.log('Saving salary data:', salaryData)
        
        let result
        if (editingItem) {
          result = await supabase
            .from('salaries')
            .update(salaryData)
            .eq('id', editingItem.id)
        } else {
          result = await supabase
            .from('salaries')
            .insert([salaryData])
        }
        
        if (result.error) {
          console.error('Salary error:', result.error)
          throw result.error
        }
        
        alert('Salary record saved successfully!')
      }
      else if (activeTab === 'invoices') {
        const invoiceData = {
          client_id: formData.client_id,
          subtotal: parseFloat(formData.subtotal) || 0,
          tax: parseFloat(formData.tax) || 0,
          total: (parseFloat(formData.subtotal) || 0) + (parseFloat(formData.tax) || 0),
          due_date: formData.due_date,
          currency: formData.currency || 'USD',
          notes: formData.notes || '',
          status: formData.status || 'draft',
          company_id: companyId
        }
        
        if (!editingItem) {
          invoiceData.invoice_number = 'INV-' + Date.now() + '-' + Math.floor(Math.random() * 1000)
        }
        
        console.log('Saving invoice data:', invoiceData)
        
        let result
        if (editingItem) {
          result = await supabase
            .from('invoices')
            .update(invoiceData)
            .eq('id', editingItem.id)
        } else {
          result = await supabase
            .from('invoices')
            .insert([invoiceData])
        }
        
        if (result.error) {
          console.error('Invoice error:', result.error)
          throw result.error
        }
        
        alert('Invoice saved successfully!')
      }
      else if (activeTab === 'budgets') {
        console.log('Budget form data:', formData)
        
        const month = parseInt(formData.month)
        const year = parseInt(formData.year)
        
        console.log('Parsed month:', month)
        console.log('Parsed year:', year)
        
        if (!month || isNaN(month) || month < 1 || month > 12) {
          alert('Please select a valid month (1-12)')
          setLoading(false)
          return
        }
        
        if (!year || isNaN(year) || year < 2000) {
          alert('Please select a valid year')
          setLoading(false)
          return
        }
        
        if (!formData.category || formData.category.trim() === '') {
          alert('Please enter a category')
          setLoading(false)
          return
        }
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
          alert('Please enter a valid budget amount')
          setLoading(false)
          return
        }
        
        const budgetData = {
          month: month,
          year: year,
          category: formData.category.trim(),
          amount: parseFloat(formData.amount) || 0,
          currency: formData.currency || 'USD',
          notes: formData.notes || '',
          company_id: companyId
        }
        
        console.log('Saving budget data:', budgetData)
        
        let result
        if (editingItem) {
          result = await supabase
            .from('budgets')
            .update(budgetData)
            .eq('id', editingItem.id)
        } else {
          result = await supabase
            .from('budgets')
            .insert([budgetData])
        }
        
        if (result.error) {
          console.error('Budget error:', result.error)
          throw result.error
        }
        
        alert('Budget saved successfully!')
      }
      else if (activeTab === 'taxReturns') {
        const taxData = { 
          tax_type: formData.tax_type || '',
          period: formData.period || '',
          amount_due: parseFloat(formData.amount_due) || 0,
          due_date: formData.due_date || null,
          status: formData.status || 'pending',
          currency: formData.currency || 'USD',
          company_id: companyId 
        }
        
        console.log('Saving tax data:', taxData)
        
        let result
        if (editingItem) {
          result = await supabase
            .from('tax_returns')
            .update(taxData)
            .eq('id', editingItem.id)
        } else {
          result = await supabase
            .from('tax_returns')
            .insert([taxData])
        }
        
        if (result.error) {
          console.error('Tax error:', result.error)
          throw result.error
        }
        
        alert('Tax record saved successfully!')
      }

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      
      await fetchData()
      
    } catch (err) {
      console.error('Submit error:', err)
      alert('Error: ' + (err.message || 'Unknown error occurred'))
    }
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (confirm('Are you sure?')) {
      if (activeTab === 'transactions') await supabase.from('transactions').delete().eq('id', id)
      else if (activeTab === 'salaries') await supabase.from('salaries').delete().eq('id', id)
      else if (activeTab === 'invoices') await supabase.from('invoices').delete().eq('id', id)
      else if (activeTab === 'budgets') await supabase.from('budgets').delete().eq('id', id)
      else if (activeTab === 'taxReturns') await supabase.from('tax_returns').delete().eq('id', id)
      else if (activeTab === 'documents') await supabase.from('documents').delete().eq('id', id)
      fetchData()
    }
  }

 // Send Email Handler - Using your Cloudflare Worker

const handleSendEmail = async (e) => {
  if (e && e.preventDefault) {
    e.preventDefault()
  }
  
  console.log('Email form submitted')
  setLoading(true)
  
  try {
    const { to, subject, body, invoice_id } = emailCompose
    
    if (!to || !subject || !body) {
      alert('Please fill in all required fields')
      setLoading(false)
      return
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(to)) {
      alert('Please enter a valid email address')
      setLoading(false)
      return
    }
    
    // 1. Save email to database
    const emailData = {
      from_email: session.user.email,
      to_email: to,
      subject: subject,
      body: body,
      company_id: companyId,
      agent_id: session.user.id,
      client_id: emailCompose.client_id || null,
      direction: 'outbound',
      status: 'sending',
      created_at: new Date().toISOString()
    }
    
    console.log('Saving email to database:', emailData)
    
    const { data: savedEmail, error: saveError } = await supabase
      .from('emails')
      .insert([emailData])
      .select()
    
    if (saveError) {
      console.error('Save error:', saveError)
      throw saveError
    }
    
    console.log('Email saved to database:', savedEmail)
    
    // 2. Send email using your Cloudflare Worker
    // Note: The worker expects 'html' or 'text' instead of 'body'
    const workerUrl = 'https://email-proxy.raola86.workers.dev'
    
    console.log('Calling Cloudflare Worker:', workerUrl)
    
    const response = await fetch(workerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: to,
        subject: subject,
        html: body.replace(/\n/g, '<br>'), // Convert newlines to HTML line breaks
        from: session.user.email
      })
    })
    
    // Check if response is ok
    if (!response.ok) {
      let errorMessage = 'Failed to send email'
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorData.message || errorMessage
        console.error('Worker error:', errorData)
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError)
      }
      throw new Error(errorMessage)
    }
    
    // Parse the response
    let result
    try {
      result = await response.json()
      console.log('Email sent successfully via worker:', result)
    } catch (parseError) {
      console.error('Failed to parse response:', parseError)
      throw new Error('Invalid response from email service')
    }
    
    // 3. Update email status to 'sent'
    if (savedEmail && savedEmail[0] && savedEmail[0].id) {
      await supabase
        .from('emails')
        .update({ status: 'sent' })
        .eq('id', savedEmail[0].id)
    }
    
    // 4. Update invoice if attached
    if (invoice_id) {
      await supabase
        .from('invoices')
        .update({ status: 'sent' })
        .eq('id', invoice_id)
    }
    
    alert('✅ Email sent successfully!')
    setShowEmailModal(false)
    setEmailCompose({ 
      to: '', 
      subject: '', 
      body: '', 
      client_id: null, 
      invoice_id: '' 
    })
    setSelectedClient(null)
    
    await fetchEmails()
    await fetchData()
    
  } catch (err) {
    console.error('Send email error:', err)
    alert('Error sending email: ' + (err.message || 'Unknown error occurred'))
  }
  setLoading(false)
}

   // Fetch email history
  const fetchEmails = async () => {
  if (!companyId) return
  
  try {
    const { data, error } = await supabase
      .from('emails')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(50)
    
    if (error) throw error
    setEmailHistory(data || [])
    console.log('Emails fetched:', data?.length || 0)
  } catch (err) {
    console.error('Error fetching emails:', err)
    setEmailHistory([])
  }
}

  const handleApproveSalary = async (salary) => {
    setSelectedSalary(salary)
    setShowApprovalModal(true)
  }

  const confirmApproveSalary = async () => {
    if (!selectedSalary) return
    
    try {
      await supabase
        .from('salaries')
        .update({ 
          status: 'approved', 
          approved_by: session.user.id,
          approved_at: new Date(),
          approval_note: approvalNote || 'Approved'
        })
        .eq('id', selectedSalary.id)
      
      alert('Salary approved successfully!')
      setShowApprovalModal(false)
      setSelectedSalary(null)
      setApprovalNote('')
      fetchData()
    } catch (err) {
      alert('Error approving salary: ' + err.message)
    }
  }

  const generateReport = () => {
    let startDate, endDate
    if (selectedPeriod === 'monthly') {
      startDate = new Date(selectedYear, selectedMonth - 1, 1)
      endDate = new Date(selectedYear, selectedMonth, 0)
    } else if (selectedPeriod === 'quarterly') {
      const startMonth = (selectedQuarter - 1) * 3
      startDate = new Date(selectedYear, startMonth, 1)
      endDate = new Date(selectedYear, startMonth + 3, 0)
    } else {
      startDate = new Date(selectedYear, 0, 1)
      endDate = new Date(selectedYear, 11, 31)
    }

    const filtered = transactions.filter(t => {
      const date = new Date(t.transaction_date)
      return date >= startDate && date <= endDate
    })
    const totalInc = filtered.filter(t => t.type === 'income').reduce((s, t) => s + (t.amount || 0), 0)
    const totalExp = filtered.filter(t => t.type === 'expense').reduce((s, t) => s + (t.amount || 0), 0)

    setReportData({
      label: selectedPeriod === 'monthly' ? months[selectedMonth - 1] : selectedPeriod === 'quarterly' ? `Q${selectedQuarter}` : `FY ${selectedYear}`,
      year: selectedYear,
      startDate: startDate.toLocaleDateString(),
      endDate: endDate.toLocaleDateString(),
      totalIncome: totalInc,
      totalExpenses: totalExp,
      profit: totalInc - totalExp,
      companyName: companyName,
      transactions: filtered
    })
    setShowReportModal(true)
  }

  const downloadPDF = () => {
    if (!reportData) return
    const win = window.open('', '_blank')
    win.document.write(`
      <html>
        <head>
          <title>Financial Report - ${reportData.companyName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1 { color: #1e3a5f; }
            .company-header { text-align: center; margin-bottom: 30px; }
            table { border-collapse: collapse; width: 100%; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #1e3a5f; color: white; }
            .profit { color: green; }
            .loss { color: red; }
          </style>
        </head>
        <body>
          <div class="company-header">
            <h1>${reportData.companyName}</h1>
            <h2>Financial Report - ${reportData.label} ${reportData.year}</h2>
          </div>
          <p>Period: ${reportData.startDate} to ${reportData.endDate}</p>
          <p><strong>Total Income:</strong> $${reportData.totalIncome.toLocaleString()}</p>
          <p><strong>Total Expenses:</strong> $${reportData.totalExpenses.toLocaleString()}</p>
          <p class="${reportData.profit >= 0 ? 'profit' : 'loss'}"><strong>Net Profit: $${reportData.profit.toLocaleString()}</strong></p>
          <h3>Transactions</h3>
          <table>
            <thead>
              <tr><th>Date</th><th>Description</th><th>Type</th><th>Category</th><th>Amount</th></tr>
            </thead>
            <tbody>
              ${reportData.transactions.map(t => `
                <tr>
                  <td>${new Date(t.transaction_date).toLocaleDateString()}</td>
                  <td>${t.description || ''}</td>
                  <td>${t.type || ''}</td>
                  <td>${t.category || ''}</td>
                  <td>${t.currency || 'USD'} ${(t.amount || 0).toLocaleString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>${reportData.companyName} - Powered by EstatesFlow</p>
        </body>
      </html>
    `)
    win.document.close()
    win.print()
  }

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = { USD: '$', EUR: '€', NGN: '₦' }
    return `${symbols[currency] || '$'}${(amount || 0).toLocaleString()}`
  }

  // Get table headers
  const getTableHeaders = () => {
    if (activeTab === 'transactions') return ['Date', 'Description', 'Type', 'Category', 'Amount']
    if (activeTab === 'invoices') return ['Invoice #', 'Client', 'Amount', 'Due Date', 'Status']
    if (activeTab === 'salaries') return ['Agent', 'Period', 'Amount', 'Status']
    return ['Data']
  }

  // Export to Excel
 
  const exportToExcel = () => {
  let data = []
  let headers = []
  let sheetName = ''
  
  if (activeTab === 'transactions') {
    headers = ['Date', 'Description', 'Type', 'Category', 'Amount', 'Currency']
    data = transactions.map(t => [t.transaction_date, t.description, t.type, t.category, t.amount, t.currency])
    sheetName = 'Transactions'
  } else if (activeTab === 'salaries') {
    headers = ['Agent', 'Month', 'Year', 'Base Salary', 'Commission', 'Bonus', 'Deductions', 'Net Salary', 'Status']
    data = salaries.map(s => {
      const agent = agents.find(a => a.id === s.agent_id)
      return [agent?.email || 'N/A', months[s.month - 1], s.year, s.base_salary, s.commission, s.bonus, s.deductions, s.net_salary, s.status]
    })
    sheetName = 'Salaries'
  } else if (activeTab === 'invoices') {
    headers = ['Invoice #', 'Client', 'Total', 'Due Date', 'Status']
    data = invoices.map(i => {
      const client = clients.find(c => c.id === i.client_id)
      return [i.invoice_number, client?.full_name || 'N/A', i.total, new Date(i.due_date).toLocaleDateString(), i.status]
    })
    sheetName = 'Invoices'
  } else if (activeTab === 'budgets') {
    headers = ['Month', 'Year', 'Category', 'Amount', 'Currency']
    data = budgets.map(b => [months[b.month - 1], b.year, b.category, b.amount, b.currency])
    sheetName = 'Budgets'
  } else if (activeTab === 'taxReturns') {
    headers = ['Tax Type', 'Period', 'Amount Due', 'Status', 'Due Date']
    data = taxReturns.map(t => [t.tax_type, t.period, t.amount_due, t.status, t.due_date])
    sheetName = 'Tax Returns'
  } else {
    headers = ['Data']
    data = []
    sheetName = 'Export'
  }

  if (data.length === 0) {
    alert('No data to export!')
    return
  }

  const wsData = [headers, ...data]
  const ws = XLSX.utils.aoa_to_sheet(wsData)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName)
  XLSX.writeFile(wb, `${companyName}_${sheetName}_${new Date().toISOString().split('T')[0]}.xlsx`)
}

  // Export to PDF
  const exportToPDF = (data, title, filename) => {
    const doc = new jsPDF()
    const company = companyName || ''
    
    doc.setFontSize(20)
    doc.text(`${company} - ${title}`, 14, 20)
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30)
    doc.text(`Generated by: ${session.user.email}`, 14, 37)
    
    const headers = getTableHeaders()
    const tableData = data.map(item => {
      if (activeTab === 'transactions') {
        return [item.transaction_date, item.description, item.type, item.category, `${item.currency} ${item.amount}`]
      }
      if (activeTab === 'invoices') {
        const client = clients.find(c => c.id === item.client_id)
        return [item.invoice_number, client?.full_name || 'N/A', `${item.currency} ${item.total}`, new Date(item.due_date).toLocaleDateString(), item.status]
      }
      if (activeTab === 'salaries') {
        const agent = agents.find(a => a.id === item.agent_id)
        return [agent?.email || 'N/A', `${months[item.month - 1]} ${item.year}`, `${item.currency} ${item.net_salary}`, item.status]
      }
      return Object.values(item)
    })
    
    if (doc.autoTable) {
      doc.autoTable({
        head: [headers],
        body: tableData,
        startY: 45,
        theme: 'striped',
        headStyles: { fillColor: [30, 58, 95] }
      })
    } else {
      doc.text('Data export available in Excel format', 14, 50)
    }
    
    doc.save(`${company}_${filename}_${new Date().toISOString().split('T')[0]}.pdf`)
  }

  // Import from CSV
  const importFromCSV = (file) => {
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = async (e) => {
      const data = e.target.result
      const workbook = XLSX.read(data, { type: 'binary' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const jsonData = XLSX.utils.sheet_to_json(worksheet)
      
      for (const row of jsonData) {
        const importData = {
          ...row,
          company_id: companyId,
          created_at: new Date()
        }
        
        if (activeTab === 'transactions') {
          await supabase.from('transactions').insert(importData)
        } else if (activeTab === 'invoices') {
          await supabase.from('invoices').insert(importData)
        } else if (activeTab === 'salaries') {
          await supabase.from('salaries').insert(importData)
        } else if (activeTab === 'budgets') {
          await supabase.from('budgets').insert(importData)
        }
      }
      
      alert(`Imported ${jsonData.length} records successfully!`)
      fetchData()
    }
    reader.readAsBinaryString(file)
  }

  // Handle salary approval modal
  const ApprovalModal = () => (
    <div style={modalOverlay} onClick={() => setShowApprovalModal(false)}>
      <div style={{ ...modalStyle, maxWidth: 450 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: '0 0 20px 0' }}>💰 Approve Salary</h2>
        <p><strong>Agent:</strong> {agents.find(a => a.id === selectedSalary?.agent_id)?.email || 'N/A'}</p>
        <p><strong>Amount:</strong> {formatCurrency(selectedSalary?.net_salary, selectedSalary?.currency)}</p>
        <p><strong>Period:</strong> {selectedSalary ? months[selectedSalary.month - 1] : ''} {selectedSalary?.year}</p>
        <div style={{ margin: '16px 0' }}>
          <label style={labelStyle}>Approval Note</label>
          <input
            type="text"
            placeholder="Approval note (optional)"
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={confirmApproveSalary} style={{ ...modalBtnStyle, background: '#059669', color: '#fff' }}>
            ✅ Approve
          </button>
          <button onClick={() => setShowApprovalModal(false)} style={{ ...modalBtnStyle, background: '#6b7280', color: '#fff' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

   // Email Modal Component - Optimized with useCallback
 const EmailModal = () => {
  // Use local state for the form to prevent re-renders
  const [localEmail, setLocalEmail] = useState({
    to: emailCompose.to || '',
    subject: emailCompose.subject || '',
    body: emailCompose.body || '',
    invoice_id: emailCompose.invoice_id || ''
  })

  // Update local state when emailCompose changes (for client quick actions)
  useEffect(() => {
    setLocalEmail({
      to: emailCompose.to || '',
      subject: emailCompose.subject || '',
      body: emailCompose.body || '',
      invoice_id: emailCompose.invoice_id || ''
    })
  }, [emailCompose.to, emailCompose.subject, emailCompose.body, emailCompose.invoice_id])

  const handleLocalChange = (field, value) => {
    setLocalEmail(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleLocalSubmit = async (e) => {
    e.preventDefault()
    
    // Update the main emailCompose state with local values
    setEmailCompose({
      ...emailCompose,
      to: localEmail.to,
      subject: localEmail.subject,
      body: localEmail.body,
      invoice_id: localEmail.invoice_id
    })
    
    // Call the original handleSendEmail
    await handleSendEmail(e)
  }

  return (
    <div style={modalOverlay} onClick={() => setShowEmailModal(false)}>
      <div style={{ ...modalStyle, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: 0 }}>
            📧 {selectedClient ? `Email to ${selectedClient.full_name}` : 'Compose New Email'}
          </h2>
          <button 
            onClick={() => setShowEmailModal(false)} 
            style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleLocalSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>To *</label>
            <input
              type="email"
              placeholder="recipient@email.com"
              value={localEmail.to}
              onChange={(e) => handleLocalChange('to', e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>From</label>
            <input
              type="email"
              value={session?.user?.email || 'Not available'}
              disabled
              style={{...inputStyle, background: '#f1f5f9', cursor: 'not-allowed', color: '#64748b'}}
            />
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Subject *</label>
            <input
              type="text"
              placeholder="Email subject"
              value={localEmail.subject}
              onChange={(e) => handleLocalChange('subject', e.target.value)}
              required
              style={inputStyle}
            />
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Message *</label>
            <textarea
              placeholder="Type your message here..."
              value={localEmail.body}
              onChange={(e) => handleLocalChange('body', e.target.value)}
              required
              rows="6"
              style={{...inputStyle, resize: 'vertical', fontFamily: 'inherit'}}
            />
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Attach Invoice (Optional)</label>
            <select
              value={localEmail.invoice_id || ''}
              onChange={(e) => handleLocalChange('invoice_id', e.target.value)}
              style={inputStyle}
            >
              <option value="">No invoice attached</option>
              {invoices.filter(i => i.status !== 'paid').slice(0, 10).map(inv => {
                const client = clients.find(c => c.id === inv.client_id)
                return (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {client?.full_name || 'N/A'} (${inv.total?.toLocaleString()})
                  </option>
                )
              })}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" style={{...saveBtn, opacity: loading ? 0.7 : 1}} disabled={loading}>
              {loading ? '⏳ Sending...' : '📤 Send Email'}
            </button>
            <button type="button" onClick={() => setShowEmailModal(false)} style={cancelBtn}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

  // Styles
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const statCard = { background: '#fff', padding: 20, borderRadius: 16, textAlign: 'center', border: '1px solid #e2e8f0' }
  const statNumber = { fontSize: 32, fontWeight: 'bold', color: '#1e3a5f', marginTop: 10 }
  const chartCard = { background: '#fff', borderRadius: 20, padding: 20, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }
  const filterCard = { background: '#fff', padding: 20, borderRadius: 16, marginBottom: 20 }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 25, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
  const importBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 25, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }
  const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', marginRight: 8, fontSize: 13 }
  const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }
  const inputStyle = { width: '100%', padding: 10, margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }
  const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px', color: '#1e293b' }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }
  const modalStyle = { background: '#fff', padding: 25, borderRadius: 20, width: '90%', maxWidth: 550, maxHeight: '80vh', overflow: 'auto' }
  const modalBtnStyle = { padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }
  const tableStyle = { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden' }

  // Render form
  const renderForm = () => {
    const style = { ...inputStyle }
    
    if (activeTab === 'transactions') {
      return (
        <>
          <select value={formData.type || ''} onChange={e => setFormData({...formData, type: e.target.value})} required style={style}>
            <option value="">Select Type</option>
            <option value="income">💰 Income</option>
            <option value="expense">📉 Expense</option>
          </select>
          <input placeholder="Description" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} required style={style} />
          <input placeholder="Amount" type="number" step="0.01" value={formData.amount || ''} onChange={e => setFormData({...formData, amount: parseFloat(e.target.value)})} required style={style} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={style}>
            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={formData.category || ''} onChange={e => setFormData({...formData, category: e.target.value})} style={style}>
            <option value="">Select Category</option>
            {(formData.type === 'income' ? incomeCategories : expenseCategories).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={formData.transaction_date || new Date().toISOString().split('T')[0]} onChange={e => setFormData({...formData, transaction_date: e.target.value})} style={style} />
          <input placeholder="Notes (optional)" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={style} />
        </>
      )
    }
    
    if (activeTab === 'salaries') {
      return (
        <>
          <select 
            value={formData.agent_id || ''} 
            onChange={e => setFormData({...formData, agent_id: e.target.value})} 
            required 
            style={style}
          >
            <option value="">Select Agent</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
          </select>
          <select 
            value={formData.month || ''} 
            onChange={e => {
              const val = e.target.value;
              setFormData({...formData, month: val ? parseInt(val) : ''});
            }} 
            required 
            style={style}
          >
            <option value="">Select Month</option>
            {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select 
            value={formData.year || ''} 
            onChange={e => {
              const val = e.target.value;
              setFormData({...formData, year: val ? parseInt(val) : ''});
            }} 
            required 
            style={style}
          >
            <option value="">Select Year</option>
            {[2023, 2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select 
            value={formData.currency || 'USD'} 
            onChange={e => setFormData({...formData, currency: e.target.value})} 
            style={style}
          >
            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input 
            placeholder="Base Salary" 
            type="number" 
            step="0.01" 
            value={formData.base_salary || ''} 
            onChange={e => setFormData({...formData, base_salary: parseFloat(e.target.value)})} 
            required 
            style={style} 
          />
          <input 
            placeholder="Commission" 
            type="number" 
            step="0.01" 
            value={formData.commission || 0} 
            onChange={e => setFormData({...formData, commission: parseFloat(e.target.value)})} 
            style={style} 
          />
          <input 
            placeholder="Bonus" 
            type="number" 
            step="0.01" 
            value={formData.bonus || 0} 
            onChange={e => setFormData({...formData, bonus: parseFloat(e.target.value)})} 
            style={style} 
          />
          <input 
            placeholder="Deductions" 
            type="number" 
            step="0.01" 
            value={formData.deductions || 0} 
            onChange={e => setFormData({...formData, deductions: parseFloat(e.target.value)})} 
            style={style} 
          />
        </>
      )
    }
    
    if (activeTab === 'invoices') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({...formData, client_id: e.target.value})} required style={style}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input placeholder="Subtotal" type="number" value={formData.subtotal || ''} onChange={e => { 
            const sub = parseFloat(e.target.value) || 0; 
            setFormData({...formData, subtotal: sub, tax: sub * 0.075, total: sub * 1.075 }) 
          }} required style={style} />
          <input placeholder="Due Date" type="date" value={formData.due_date || ''} onChange={e => setFormData({...formData, due_date: e.target.value})} required style={style} />
          <select value={formData.currency || 'USD'} onChange={e => setFormData({...formData, currency: e.target.value})} style={style}>
            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea placeholder="Notes" rows="2" value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} style={{...style, resize: 'vertical'}} />
        </>
      )
    }
    
    if (activeTab === 'budgets') {
      return (
        <>
          <select 
            value={formData.month || ''} 
            onChange={e => {
              const val = e.target.value;
              setFormData({...formData, month: val ? parseInt(val) : ''});
            }} 
            required 
            style={style}
          >
            <option value="">Select Month</option>
            {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          
          <select 
            value={formData.year || ''} 
            onChange={e => {
              const val = e.target.value;
              setFormData({...formData, year: val ? parseInt(val) : ''});
            }} 
            required 
            style={style}
          >
            <option value="">Select Year</option>
            {[2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          
          <input 
            placeholder="Category" 
            value={formData.category || ''} 
            onChange={e => setFormData({...formData, category: e.target.value})} 
            required 
            style={style} 
          />
          
          <input 
            placeholder="Budget Amount" 
            type="number" 
            step="0.01" 
            value={formData.amount || ''} 
            onChange={e => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} 
            required 
            style={style} 
          />
          
          <select 
            value={formData.currency || 'USD'} 
            onChange={e => setFormData({...formData, currency: e.target.value})} 
            style={style}
          >
            {currencies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          
          <input 
            placeholder="Notes (optional)" 
            value={formData.notes || ''} 
            onChange={e => setFormData({...formData, notes: e.target.value})} 
            style={style} 
          />
        </>
      )
    }
    
    if (activeTab === 'taxReturns') {
      return (
        <>
          <select value={formData.tax_type || ''} onChange={e => setFormData({...formData, tax_type: e.target.value})} required style={style}>
            <option value="">Select Tax Type</option>
            <option value="vat">VAT (7.5%)</option>
            <option value="corporate">Corporate Tax (25%)</option>
            <option value="payroll">Payroll Tax (10%)</option>
          </select>
          <input placeholder="Period (e.g., Q1 2025)" value={formData.period || ''} onChange={e => setFormData({...formData, period: e.target.value})} required style={style} />
          <input placeholder="Amount Due" type="number" value={formData.amount_due || ''} onChange={e => setFormData({...formData, amount_due: parseFloat(e.target.value)})} required style={style} />
          <input type="date" placeholder="Due Date" value={formData.due_date || ''} onChange={e => setFormData({...formData, due_date: e.target.value})} style={style} />
          <select value={formData.status || 'pending'} onChange={e => setFormData({...formData, status: e.target.value})} style={style}>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </>
      )
    }
    return null
  }

  // Render list
 // Render list
const renderList = () => {
  if (loading) return <div style={{textAlign: 'center', padding: 40}}>⏳ Loading {activeTab}...</div>
  
  const cardStyle = { background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }
  
  // ===== TRANSACTIONS =====
  if (activeTab === 'transactions') {
    if (!transactions.length) return <p>No transactions. Click "+ Add New" to create one.</p>
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>➕ Add New</button>
          <button onClick={exportToExcel} style={{ ...addBtn, background: '#059669' }}>📥 Export Excel</button>
          <button onClick={() => exportToPDF(transactions, 'Transactions', 'transactions')} style={{ ...addBtn, background: '#2c7da0' }}>📄 Export PDF</button>
          <label style={importBtn}>
            📤 Import CSV
            <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => importFromCSV(e.target.files[0])} style={{ display: 'none' }} />
          </label>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>Date</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Description</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Type</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Category</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Amount</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 10 }}>{new Date(t.transaction_date).toLocaleDateString()}</td>
                  <td style={{ padding: 10 }}>{t.description}</td>
                  <td style={{ padding: 10 }}>
                    <span style={{ background: t.type === 'income' ? '#d1fae5' : '#fee2e2', padding: '4px 12px', borderRadius: 20 }}>
                      {t.type === 'income' ? '💰 Income' : '📉 Expense'}
                    </span>
                  </td>
                  <td style={{ padding: 10 }}>{t.category}</td>
                  <td style={{ padding: 10, fontWeight: 'bold', color: t.type === 'income' ? '#059669' : '#dc2626' }}>
                    {t.currency} {t.amount?.toLocaleString()}
                  </td>
                  <td style={{ padding: 10 }}>
                    <button onClick={() => { setEditingItem(t); setFormData(t); setShowModal(true) }} style={editBtn}>Edit</button>
                    <button onClick={() => handleDelete(t.id)} style={deleteBtn}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ===== SALARIES =====
  if (activeTab === 'salaries') {
    if (!salaries.length) return <p>No salary records. Click "+ Add New" to create one.</p>
    
    const pendingSalariesList = salaries.filter(s => s.status === 'pending')
    const approvedSalariesList = salaries.filter(s => s.status === 'approved')
    const paidSalariesList = salaries.filter(s => s.status === 'paid')
    
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>➕ Add New</button>
          <button onClick={exportToExcel} style={{ ...addBtn, background: '#059669' }}>📥 Export Excel</button>
        </div>
        
        {pendingSalariesList.length > 0 && (
          <>
            <h3 style={{ marginTop: 20, color: '#f59e0b' }}>⏳ Pending Approval ({pendingSalariesList.length})</h3>
            <div style={responsiveStyles.grid}>
              {pendingSalariesList.map(s => {
                const agent = agents.find(a => a.id === s.agent_id)
                return (
                  <div key={s.id} style={cardStyle}>
                    <h4 style={{ margin: '0 0 8px 0' }}>{agent?.email || 'Unknown Agent'}</h4>
                    <p><strong>{months[s.month - 1]} {s.year}</strong></p>
                    <p>💰 Net Salary: {formatCurrency(s.net_salary, s.currency)}</p>
                    <p>Status: <span style={{ background: '#fed7aa', padding: '4px 12px', borderRadius: 20 }}>⏳ Pending</span></p>
                    <button onClick={() => handleApproveSalary(s)} style={{ ...editBtn, background: '#059669' }}>✅ Approve</button>
                    <button onClick={() => handleDelete(s.id)} style={deleteBtn}>Delete</button>
                  </div>
                )
              })}
            </div>
          </>
        )}
        
        {approvedSalariesList.length > 0 && (
          <>
            <h3 style={{ marginTop: 20, color: '#059669' }}>✅ Approved ({approvedSalariesList.length})</h3>
            <div style={responsiveStyles.grid}>
              {approvedSalariesList.map(s => {
                const agent = agents.find(a => a.id === s.agent_id)
                return (
                  <div key={s.id} style={cardStyle}>
                    <h4 style={{ margin: '0 0 8px 0' }}>{agent?.email || 'Unknown Agent'}</h4>
                    <p><strong>{months[s.month - 1]} {s.year}</strong></p>
                    <p>💰 Net Salary: {formatCurrency(s.net_salary, s.currency)}</p>
                    <p>Status: <span style={{ background: '#d1fae5', padding: '4px 12px', borderRadius: 20 }}>✅ Approved</span></p>
                    <button onClick={() => handleDelete(s.id)} style={deleteBtn}>Delete</button>
                  </div>
                )
              })}
            </div>
          </>
        )}
        
        {paidSalariesList.length > 0 && (
          <>
            <h3 style={{ marginTop: 20, color: '#64748b' }}>💳 Paid ({paidSalariesList.length})</h3>
            <div style={responsiveStyles.grid}>
              {paidSalariesList.map(s => {
                const agent = agents.find(a => a.id === s.agent_id)
                return (
                  <div key={s.id} style={cardStyle}>
                    <h4 style={{ margin: '0 0 8px 0' }}>{agent?.email || 'Unknown Agent'}</h4>
                    <p><strong>{months[s.month - 1]} {s.year}</strong></p>
                    <p>💰 Net Salary: {formatCurrency(s.net_salary, s.currency)}</p>
                    <p>Status: <span style={{ background: '#e2e8f0', padding: '4px 12px', borderRadius: 20 }}>💳 Paid</span></p>
                    <button onClick={() => handleDelete(s.id)} style={deleteBtn}>Delete</button>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    )
  }

  // ===== INVOICES =====
  if (activeTab === 'invoices') {
    if (!invoices.length) return <p>No invoices. Click "+ Add New" to create one.</p>
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>➕ Add New</button>
          <button onClick={exportToExcel} style={{ ...addBtn, background: '#059669' }}>📥 Export Excel</button>
        </div>
        <div style={responsiveStyles.grid}>
          {invoices.map(i => {
            const client = clients.find(c => c.id === i.client_id)
            return (
              <div key={i.id} style={cardStyle}>
                <h4 style={{ margin: '0 0 8px 0' }}>{i.invoice_number}</h4>
                <p>👤 Client: {client?.full_name || 'N/A'}</p>
                <p>💰 Amount: {formatCurrency(i.total, i.currency)}</p>
                <p>📅 Due: {new Date(i.due_date).toLocaleDateString()}</p>
                <p>Status: <span style={{ 
                  background: i.status === 'paid' ? '#d1fae5' : i.status === 'sent' ? '#dbeafe' : '#fed7aa', 
                  padding: '4px 12px', borderRadius: 20 
                }}>{i.status}</span></p>
                <button onClick={() => { setEditingItem(i); setFormData(i); setShowModal(true) }} style={editBtn}>Edit</button>
                <button onClick={() => handleDelete(i.id)} style={deleteBtn}>Delete</button>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ===== BUDGETS =====
  if (activeTab === 'budgets') {
    if (!budgets.length) return <p>No budgets. Click "+ Add New" to create one.</p>
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>➕ Add New</button>
          <button onClick={exportToExcel} style={{ ...addBtn, background: '#059669' }}>📥 Export Excel</button>
        </div>
        <div style={responsiveStyles.grid}>
          {budgets.map(b => (
            <div key={b.id} style={cardStyle}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#1e3a5f' }}>{months[b.month - 1]} {b.year}</h4>
              <p style={{ margin: '4px 0' }}><strong>📂 Category:</strong> {b.category}</p>
              <p style={{ margin: '4px 0' }}><strong>💰 Budget:</strong> {formatCurrency(b.amount, b.currency)}</p>
              {b.notes && <p style={{ margin: '4px 0', fontSize: '12px', color: '#64748b' }}>{b.notes}</p>}
              <button onClick={() => { setEditingItem(b); setFormData(b); setShowModal(true) }} style={editBtn}>Edit</button>
              <button onClick={() => handleDelete(b.id)} style={deleteBtn}>Delete</button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ===== TAX RETURNS =====
  if (activeTab === 'taxReturns') {
    if (!taxReturns.length) return <p>No tax records. Click "+ Add New" to create one.</p>
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>➕ Add New</button>
          <button onClick={exportToExcel} style={{ ...addBtn, background: '#059669' }}>📥 Export Excel</button>
        </div>
        <div style={responsiveStyles.grid}>
          {taxReturns.map(t => (
            <div key={t.id} style={cardStyle}>
              <h4 style={{ margin: '0 0 8px 0' }}>🏛️ {t.tax_type?.toUpperCase()} Tax</h4>
              <p>📅 Period: {t.period}</p>
              <p>💰 Amount Due: {formatCurrency(t.amount_due, t.currency || 'USD')}</p>
              <p>Status: <span style={{ background: t.status === 'paid' ? '#d1fae5' : '#fed7aa', padding: '4px 12px', borderRadius: 20 }}>{t.status}</span></p>
              <button onClick={() => { setEditingItem(t); setFormData(t); setShowModal(true) }} style={editBtn}>Edit</button>
              <button onClick={() => handleDelete(t.id)} style={deleteBtn}>Delete</button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ===== DOCUMENTS =====
  if (activeTab === 'documents') {
    if (!documents.length) return <p>No documents. Click "Upload Document" to add one.</p>
    return (
      <div style={responsiveStyles.grid}>
        {documents.map(d => (
          <div key={d.id} style={cardStyle}>
            <h4>📄 {d.title}</h4>
            <p>📅 Uploaded: {new Date(d.created_at).toLocaleDateString()}</p>
            <a href={d.file_url} target="_blank" rel="noopener noreferrer" style={{ ...editBtn, background: '#059669', textDecoration: 'none' }}>📥 Download</a>
            <button onClick={() => handleDelete(d.id)} style={deleteBtn}>Delete</button>
          </div>
        ))}
      </div>
    )
  }
  
  return null
}
  // Get header title
  const getHeaderTitle = () => {
    if (activeTab === 'dashboard') return 'Dashboard'
    if (activeTab === 'cashflow') return 'Cash Flow'
    if (activeTab === 'profitloss') return 'Profit & Loss Statement'
    if (activeTab === 'chartofaccounts') return 'Chart of Accounts'
    if (activeTab === 'balancesheet') return 'Balance Sheet'
    if (activeTab === 'transactions') return 'Transactions'
    if (activeTab === 'salaries') return 'Salaries'
    if (activeTab === 'invoices') return 'Invoices'
    if (activeTab === 'budgets') return 'Budgets'
    if (activeTab === 'taxReturns') return 'Tax Returns'
    if (activeTab === 'documents') return 'Documents'
    if (activeTab === 'reports') return 'Reports'
    if (activeTab === 'whatsapp') return 'WhatsApp Communication'
    if (activeTab === 'emails') return 'Email Communication'
    return activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
  }

  const plData = calculatePL()

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Accounting Portal"
      portalIcon="💰"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlay} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: 25, width: '90%', maxWidth: 550 }}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && <ApprovalModal />}

      {/* Email Modal */}
      {showEmailModal && <EmailModal key={Date.now()} />}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: '28px', color: '#1e3a5f', margin: 0 }}>{getHeaderTitle()}</h1>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Welcome, {session.user.email} - {companyName}</p>
        </div>
        {activeTab !== 'dashboard' && activeTab !== 'cashflow' && activeTab !== 'profitloss' && activeTab !== 'reports' && activeTab !== 'documents' && activeTab !== 'whatsapp' && activeTab !== 'emails' && activeTab !== 'chartofaccounts' && activeTab !== 'balancesheet' && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>
            + Add New
          </button>
        )}
        {activeTab === 'documents' && (
          <button onClick={() => setShowDocModal(true)} style={addBtn}>📎 Upload Document</button>
        )}
      </div>

      {errorMsg && <div style={{ background: '#fee2e2', color: '#dc2626', padding: 10, borderRadius: 8, marginBottom: 20 }}>⚠️ {errorMsg}</div>}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          <div style={responsiveStyles.statsGrid}>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>💰 Total Revenue</h3><p style={statNumber}>${totalIncome.toLocaleString()}</p></div>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>📉 Total Expenses</h3><p style={statNumber}>${totalExpense.toLocaleString()}</p></div>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>📈 Net Profit</h3><p style={{ ...statNumber, color: netProfit >= 0 ? '#059669' : '#dc2626' }}>${netProfit.toLocaleString()}</p></div>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>⏳ Pending Salaries</h3><p style={statNumber}>${pendingSalaries.toLocaleString()}</p></div>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>📋 Receivables</h3><p style={statNumber}>${accountsReceivable.toLocaleString()}</p></div>
            <div style={statCard}><h3 style={{ fontSize: '16px', color: '#64748b' }}>📊 Transactions</h3><p style={statNumber}>{transactions.length}</p></div>
          </div>

          <div style={responsiveStyles.chartsGrid}>
            <div style={chartCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>Monthly Revenue vs Expenses - {selectedYear}</h3>
              <div style={{ height: 300 }}>
                <Bar data={{
                  labels: getMonthlyData().monthNames,
                  datasets: [
                    { label: 'Income', data: getMonthlyData().incomeData, backgroundColor: 'rgba(5, 150, 105, 0.6)', borderColor: '#059669', borderWidth: 1 },
                    { label: 'Expenses', data: getMonthlyData().expenseData, backgroundColor: 'rgba(220, 38, 38, 0.6)', borderColor: '#dc2626', borderWidth: 1 }
                  ]
                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>Income by Category</h3>
              <div style={{ height: 300 }}>
                <Pie data={{ 
                  labels: getIncomeByCategoryData().categories, 
                  datasets: [{ 
                    data: getIncomeByCategoryData().amounts, 
                    backgroundColor: ['#059669', '#2c7da0', '#1e3a5f', '#f59e0b', '#8b5cf6', '#ec489a', '#14b8a6']
                  }] 
                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>Expenses by Category</h3>
              <div style={{ height: 300 }}>
                <Doughnut data={{ 
                  labels: getExpenseByCategoryData().categories, 
                  datasets: [{ 
                    data: getExpenseByCategoryData().amounts, 
                    backgroundColor: ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#059669', '#0891b2', '#4f46e5']
                  }] 
                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
              </div>
            </div>

            <div style={chartCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>Profit Trend</h3>
              <div style={{ height: 300 }}>
                <Line data={{ 
                  labels: getMonthlyData().monthNames, 
                  datasets: [{ 
                    label: 'Net Profit', 
                    data: getMonthlyData().incomeData.map((inc, i) => inc - getMonthlyData().expenseData[i]), 
                    borderColor: '#1e3a5f', 
                    backgroundColor: 'rgba(30, 58, 95, 0.1)', 
                    fill: true, 
                    tension: 0.4 
                  }] 
                }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Flow Tab */}
      {activeTab === 'cashflow' && (
        <div>
          <div style={filterCard}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>💵 Cash Flow Analysis - {selectedYear}</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={{ ...inputStyle, width: 'auto' }}>
                {[2023, 2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={() => exportToExcel()} style={{ ...addBtn, background: '#059669' }}>📥 Export Cash Flow</button>
              <button onClick={() => exportToPDF(transactions, 'Cash Flow', 'cashflow')} style={{ ...addBtn, background: '#2c7da0' }}>📄 Export PDF</button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>Month</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Cash Inflow</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Cash Outflow</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Net Flow</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>Cumulative</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m, i) => {
                  const inflow = transactions.filter(t => t.type === 'income' && new Date(t.transaction_date).getMonth() === i && new Date(t.transaction_date).getFullYear() === selectedYear).reduce((s,t)=>s+(t.amount||0),0)
                  const outflow = transactions.filter(t => t.type === 'expense' && new Date(t.transaction_date).getMonth() === i && new Date(t.transaction_date).getFullYear() === selectedYear).reduce((s,t)=>s+(t.amount||0),0)
                  const net = inflow - outflow
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: 10, fontWeight: '500' }}>{m}</td>
                      <td style={{ padding: 10, color: '#059669' }}>${inflow.toLocaleString()}</td>
                      <td style={{ padding: 10, color: '#dc2626' }}>${outflow.toLocaleString()}</td>
                      <td style={{ padding: 10, fontWeight: 'bold', color: net >= 0 ? '#059669' : '#dc2626' }}>${net.toLocaleString()}</td>
                      <td style={{ padding: 10, fontWeight: 'bold' }}>
                        ${transactions.filter(t => new Date(t.transaction_date).getFullYear() === selectedYear && new Date(t.transaction_date).getMonth() <= i)
                          .reduce((s,t) => s + (t.type === 'income' ? (t.amount||0) : -(t.amount||0)), 0).toLocaleString()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profit & Loss Tab */}
      {activeTab === 'profitloss' && (
        <div>
          <div style={filterCard}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>📈 Profit & Loss Statement</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
              <div>
                <label style={labelStyle}>Revenue</label>
                <input 
                  type="number" 
                  value={plStatement.revenue} 
                  onChange={e => setPlStatement({...plStatement, revenue: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter total revenue"
                />
              </div>
              <div>
                <label style={labelStyle}>Cost of Goods Sold (COGS)</label>
                <input 
                  type="number" 
                  value={plStatement.cogs} 
                  onChange={e => setPlStatement({...plStatement, cogs: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter COGS"
                />
              </div>
              <div>
                <label style={labelStyle}>Operating Expenses</label>
                <input 
                  type="number" 
                  value={plStatement.operatingExpenses} 
                  onChange={e => setPlStatement({...plStatement, operatingExpenses: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter operating expenses"
                />
              </div>
              <div>
                <label style={labelStyle}>Other Income</label>
                <input 
                  type="number" 
                  value={plStatement.otherIncome} 
                  onChange={e => setPlStatement({...plStatement, otherIncome: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter other income"
                />
              </div>
              <div>
                <label style={labelStyle}>Other Expenses</label>
                <input 
                  type="number" 
                  value={plStatement.otherExpenses} 
                  onChange={e => setPlStatement({...plStatement, otherExpenses: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter other expenses"
                />
              </div>
              <div>
                <label style={labelStyle}>Tax Rate (%)</label>
                <input 
                  type="number" 
                  value={plStatement.taxRate} 
                  onChange={e => setPlStatement({...plStatement, taxRate: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="Enter tax rate"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button onClick={() => {
                setPlStatement({
                  ...plStatement,
                  revenue: totalIncome,
                  operatingExpenses: totalExpense
                })
              }} style={{ ...addBtn, background: '#2c7da0' }}>📊 Populate from Transactions</button>
              <button onClick={() => {
                setPlStatement({
                  revenue: 0,
                  cogs: 0,
                  operatingExpenses: 0,
                  otherIncome: 0,
                  otherExpenses: 0,
                  taxRate: 25
                })
              }} style={{ ...addBtn, background: '#6b7280' }}>🔄 Reset</button>
            </div>
          </div>

          {/* P&L Results */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f', marginBottom: '15px' }}>📊 Profit & Loss Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div style={statCard}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Revenue</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>${plData.revenue.toLocaleString()}</p>
              </div>
              <div style={statCard}>
                <h4 style={{ color: '#64748b', margin: 0 }}>COGS</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>${plData.cogs.toLocaleString()}</p>
              </div>
              <div style={{ ...statCard, borderColor: plData.grossProfit >= 0 ? '#059669' : '#dc2626' }}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Gross Profit</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: plData.grossProfit >= 0 ? '#059669' : '#dc2626' }}>
                  ${plData.grossProfit.toLocaleString()}
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>Margin: {plData.revenue > 0 ? ((plData.grossProfit / plData.revenue) * 100).toFixed(1) : 0}%</p>
              </div>
              <div style={statCard}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Operating Expenses</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>${plData.operatingExpenses.toLocaleString()}</p>
              </div>
              <div style={{ ...statCard, borderColor: plData.operatingProfit >= 0 ? '#059669' : '#dc2626' }}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Operating Profit</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: plData.operatingProfit >= 0 ? '#059669' : '#dc2626' }}>
                  ${plData.operatingProfit.toLocaleString()}
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>Margin: {plData.revenue > 0 ? ((plData.operatingProfit / plData.revenue) * 100).toFixed(1) : 0}%</p>
              </div>
              <div style={statCard}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Net Other Income</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: plData.totalOtherIncome >= 0 ? '#059669' : '#dc2626' }}>
                  ${plData.totalOtherIncome.toLocaleString()}
                </p>
              </div>
              <div style={{ ...statCard, borderColor: plData.netProfitBeforeTax >= 0 ? '#059669' : '#dc2626' }}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Net Profit Before Tax</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: plData.netProfitBeforeTax >= 0 ? '#059669' : '#dc2626' }}>
                  ${plData.netProfitBeforeTax.toLocaleString()}
                </p>
              </div>
              <div style={statCard}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Tax ({plData.taxRate}%)</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>${plData.taxAmount.toLocaleString()}</p>
              </div>
              <div style={{ ...statCard, borderColor: plData.netProfitAfterTax >= 0 ? '#059669' : '#dc2626', gridColumn: 'span 2' }}>
                <h4 style={{ color: '#64748b', margin: 0 }}>Net Profit After Tax</h4>
                <p style={{ fontSize: '28px', fontWeight: 'bold', color: plData.netProfitAfterTax >= 0 ? '#059669' : '#dc2626' }}>
                  ${plData.netProfitAfterTax.toLocaleString()}
                </p>
                <p style={{ fontSize: '14px', color: '#64748b' }}>Margin: {plData.revenue > 0 ? ((plData.netProfitAfterTax / plData.revenue) * 100).toFixed(1) : 0}%</p>
              </div>
            </div>

            {/* P&L Chart */}
            <div style={{ marginTop: '20px', height: '300px' }}>
              <Bar data={{
                labels: ['Revenue', 'COGS', 'Gross Profit', 'Operating Expenses', 'Operating Profit', 'Net Profit'],
                datasets: [{
                  label: 'P&L Statement',
                  data: [plData.revenue, plData.cogs, plData.grossProfit, plData.operatingExpenses, plData.operatingProfit, plData.netProfitAfterTax],
                  backgroundColor: ['#059669', '#dc2626', '#059669', '#dc2626', '#059669', '#059669'],
                  borderColor: ['#059669', '#dc2626', '#059669', '#dc2626', '#059669', '#059669'],
                  borderWidth: 1
                }]
              }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => exportToPDF([plData], 'Profit & Loss Statement', 'profit_loss')} style={{ ...addBtn, background: '#059669' }}>📄 Export PDF</button>
              <button onClick={() => {
                const wsData = [
                  ['Metric', 'Amount'],
                  ['Revenue', plData.revenue],
                  ['COGS', plData.cogs],
                  ['Gross Profit', plData.grossProfit],
                  ['Operating Expenses', plData.operatingExpenses],
                  ['Operating Profit', plData.operatingProfit],
                  ['Net Other Income', plData.totalOtherIncome],
                  ['Net Profit Before Tax', plData.netProfitBeforeTax],
                  ['Tax', plData.taxAmount],
                  ['Net Profit After Tax', plData.netProfitAfterTax]
                ]
                const ws = XLSX.utils.aoa_to_sheet(wsData)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, 'Profit_Loss')
                XLSX.writeFile(wb, `${companyName}_Profit_Loss_${new Date().toISOString().split('T')[0]}.xlsx`)
              }} style={{ ...addBtn, background: '#2c7da0' }}>📥 Export Excel</button>
            </div>
          </div>
        </div>
      )}

      {/* Chart of Accounts Tab */}
      {activeTab === 'chartofaccounts' && <ChartOfAccounts companyId={companyId} />}

      {/* Balance Sheet Tab */}
      {activeTab === 'balancesheet' && <BalanceSheet session={session} companyId={companyId} companyName={companyName} />}

      {/* Data Tabs */}
      {['transactions', 'salaries', 'invoices', 'budgets', 'taxReturns', 'documents'].includes(activeTab) && renderList()}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div>
          <div style={filterCard}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>📊 Generate Financial Report</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} style={inputStyle}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
              {selectedPeriod === 'monthly' && (
                <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} style={inputStyle}>
                  {months.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              )}
              {selectedPeriod === 'quarterly' && (
                <select value={selectedQuarter} onChange={e => setSelectedQuarter(parseInt(e.target.value))} style={inputStyle}>
                  <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
                </select>
              )}
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={inputStyle}>
                {[2023, 2024, 2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={generateReport} style={addBtn}>📊 Generate</button>
            </div>
          </div>
          {reportData && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>Report: {reportData.label} {reportData.year}</h3>
              <p><strong>Period:</strong> {reportData.startDate} - {reportData.endDate}</p>
              <p><strong>Total Income:</strong> ${reportData.totalIncome.toLocaleString()}</p>
              <p><strong>Total Expenses:</strong> ${reportData.totalExpenses.toLocaleString()}</p>
              <p style={{ fontSize: '20px', fontWeight: 'bold', color: reportData.profit >= 0 ? '#059669' : '#dc2626' }}>
                Net Profit: ${reportData.profit.toLocaleString()}
              </p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button onClick={downloadPDF} style={{ ...addBtn, background: '#059669' }}>📄 Download PDF</button>
                <button onClick={() => setShowReportModal(false)} style={{ ...addBtn, background: '#6b7280' }}>Close</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* WhatsApp Tab */}
      {activeTab === 'whatsapp' && (
        <div>
          <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📱 WhatsApp Communication</h2>
          <div style={responsiveStyles.grid}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>📄 Share Invoice</h3>
              <p>Send invoices to clients via WhatsApp</p>
              {invoices.filter(i => i.status !== 'paid').slice(0, 5).map(inv => {
                const client = clients.find(c => c.id === inv.client_id)
                return (
                  <div key={inv.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{inv.invoice_number}</strong> - {formatCurrency(inv.total, inv.currency)}</p>
                    <p>Client: {client?.full_name || 'N/A'}</p>
                    <WhatsAppButton type="invoice" data={inv} phoneNumber={client?.phone || '234'} session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>

            <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
              <h3>💰 Share Salary Info</h3>
              <p>Send salary information to agents</p>
              {salaries.filter(s => s.status === 'pending' || s.status === 'approved').slice(0, 5).map(salary => {
                const agent = agents.find(a => a.id === salary.agent_id)
                return (
                  <div key={salary.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8 }}>
                    <p><strong>{agent?.email || 'Agent'}</strong> - {formatCurrency(salary.net_salary, salary.currency)}</p>
                    <p>Status: {salary.status}</p>
                    <WhatsAppButton type="salary" data={salary} phoneNumber="" session={session} companyName={companyName} />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Emails Tab */}
  {activeTab === 'emails' && (
  <div>
    <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📧 Email Communication</h2>
    
    <div style={responsiveStyles.grid}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
        <h3>✏️ Compose New Email</h3>
        <p style={{ color: '#64748b', marginBottom: '15px' }}>Send emails to clients, agents, or other stakeholders</p>
        <button 
          onClick={() => { 
            setSelectedClient(null); 
            setEmailCompose({ 
              to: '', 
              subject: '', 
              body: '', 
              client_id: null,
              invoice_id: ''
            }); 
            setShowEmailModal(true); 
          }} 
          style={addBtn}
        >
          📧 Compose Email
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
        <h3>👥 Quick Email to Clients</h3>
        <p style={{ color: '#64748b', marginBottom: '15px' }}>Send quick emails to your clients</p>
        {clients.length > 0 ? (
          clients.slice(0, 5).map(client => (
            <div key={client.id} style={{ marginBottom: 10, padding: 10, background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{client.full_name}</strong>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{client.email}</p>
              </div>
              <button 
                onClick={() => { 
                  setSelectedClient(client); 
                  setEmailCompose({ 
                    to: client.email, 
                    subject: '', 
                    body: '', 
                    client_id: client.id,
                    invoice_id: ''
                  }); 
                  setShowEmailModal(true); 
                }} 
                style={{ ...editBtn, background: '#059669' }}
              >
                📧 Send
              </button>
            </div>
          ))
        ) : (
          <p style={{ color: '#94a3b8' }}>No clients added yet</p>
        )}
        {clients.length > 5 && (
          <p style={{ color: '#64748b', fontSize: '13px', marginTop: '10px' }}>
            + {clients.length - 5} more clients
          </p>
        )}
      </div>
    </div>

    {/* Email History */}
    <div style={{ marginTop: '20px', background: '#fff', borderRadius: 16, padding: 20, border: '1px solid #e2e8f0' }}>
      <h3 style={{ marginBottom: '15px' }}>📨 Recent Emails</h3>
      {!emailHistory || emailHistory.length === 0 ? (
        <p style={{ color: '#94a3b8' }}>No emails sent yet</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>Date</th>
                <th style={{ padding: 12, textAlign: 'left' }}>To</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Subject</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Direction</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {emailHistory.map(email => (
                <tr key={email.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 10 }}>
                    {new Date(email.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: 10 }}>{email.to_email}</td>
                  <td style={{ padding: 10 }}><strong>{email.subject}</strong></td>
                  <td style={{ padding: 10 }}>
                    <span style={{ 
                      background: email.direction === 'outbound' ? '#dbeafe' : '#fef3c7',
                      padding: '4px 12px', 
                      borderRadius: 20,
                      fontSize: '12px'
                    }}>
                      {email.direction === 'outbound' ? '📤 Outbound' : '📥 Inbound'}
                    </span>
                  </td>
                  <td style={{ padding: 10 }}>
                    <button 
                      onClick={() => {
                        alert(
                          `From: ${email.from_email}\n` +
                          `To: ${email.to_email}\n` +
                          `Subject: ${email.subject}\n` +
                          `Date: ${new Date(email.created_at).toLocaleString()}\n\n` +
                          `${email.body}`
                        )
                      }}
                      style={{ ...editBtn, background: '#2c7da0' }}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}
      {/* Add/Edit Modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: '0 0 20px 0' }}>
              {editingItem ? '✏️ Edit' : '➕ Add New'} {activeTab === 'salaries' ? 'Salary' : activeTab === 'budgets' ? 'Budget' : activeTab.slice(0, -1)}
            </h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
{showDocModal && (
  <div style={modalOverlay} onClick={() => setShowDocModal(false)}>
    <div style={{ ...modalStyle, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
      <h2 style={{ fontSize: '22px', color: '#1e3a5f', margin: '0 0 20px 0' }}>📎 Upload Document</h2>
      <input
        type="file"
        accept=".pdf,.xlsx,.xls,.doc,.docx,.jpg,.png"
        onChange={async (e) => {
          const file = e.target.files[0]
          if (!file) return

          setUploading(true)
          try {
            const fileName = `accounting_${companyId}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`

            // 1. Upload file to storage
            const { error: uploadError } = await supabase.storage
              .from('accounting_docs')
              .upload(fileName, file)

            if (uploadError) {
              alert('Upload failed: ' + uploadError.message)
              setUploading(false)
              return
            }

            // 2. Get public URL — NO .then() here, it's synchronous in Supabase v2
            const { data: urlData } = supabase.storage
              .from('accounting_docs')
              .getPublicUrl(fileName)

            // 3. Insert row into documents table
            const { error: dbError } = await supabase
              .from('documents')
              .insert({
                title: file.name,
                file_url: urlData.publicUrl,
                uploaded_by: session.user.id,
                company_id: companyId
              })

            if (dbError) {
              alert('Database error: ' + dbError.message)
              setUploading(false)
              return
            }

            alert('✅ Document uploaded!')
            fetchData()
            setShowDocModal(false)
          } catch (err) {
            alert('Unexpected error: ' + err.message)
          }
          setUploading(false)
        }}
        style={inputStyle}
      />
      {uploading && <p>Uploading...</p>}
      <button onClick={() => setShowDocModal(false)} style={cancelBtn}>Cancel</button>
    </div>
  </div>
)}
    </PortalLayout>
  )
}

export default AccountingPortal